import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Egg, ArrowLeft, Save, Loader2, Upload, X } from 'lucide-react';
import SpeciesSelect from '../components/SpeciesSelect';
import MorphSelect from '../components/MorphSelect';

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planeada' },
  { value: 'paired', label: 'Pareja formada' },
  { value: 'ovulating', label: 'Pre-ovulacion' },
  { value: 'laid', label: 'Puesta' },
  { value: 'incubating', label: 'Incubando' },
];

export default function CreateClutchPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    category_slug: '', species_name: '', species_canonical: '',
    morph_expected: '', morph_canonical: '',
    sire_info: '', dam_info: '', expected_count: '',
    estimated_hatch_date: '', price_range_min: '', price_range_max: '',
    genetic_probability: '', breeder_notes: '', parent_images: [],
    status: 'planned', waitlist_mode: 'free', deposit_amount: '',
    shipping_methods: ['enbixos', 'pickup'],
  });

  useEffect(() => { api.getCategories().then(setCategories).catch(() => {}); }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadFile(file);
      const url = `/api/files/${result.id}`;
      setForm(f => ({ ...f, parent_images: [...f.parent_images, url] }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category_slug || !form.species_name) { toast.error('Categoria y especie son obligatorios'); return; }
    setLoading(true);
    try {
      const data = {
        ...form,
        expected_count: parseInt(form.expected_count) || 0,
        price_range_min: parseFloat(form.price_range_min) || 0,
        price_range_max: parseFloat(form.price_range_max) || 0,
        deposit_amount: parseFloat(form.deposit_amount) || 0,
      };
      await api.createClutch(data);
      toast.success('Camada publicada');
      navigate('/dashboard/camadas');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="create-clutch-page" className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-2xl font-light tracking-tight text-zinc-100 font-[Outfit]">
            <Egg className="w-6 h-6 inline mr-2 text-emerald-400" />
            Nueva Camada
          </h1>
          <p className="text-zinc-500 mt-1">Publica una camada futura para pre-venta</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Species */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Especie y Genetica</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Categoria *</Label>
              <Select value={form.category_slug} onValueChange={v => setForm(f => ({ ...f, category_slug: v, species_name: '', species_canonical: '', morph_expected: '', morph_canonical: '' }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100" data-testid="clutch-category">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent className="bg-[#121215] border-white/10">
                  {categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <SpeciesSelect categorySlug={form.category_slug} value={form.species_canonical}
              onChange={(canonical, display, isCustom) => setForm(f => ({ ...f, species_canonical: canonical === 'custom' ? '' : canonical, species_name: display, morph_expected: '', morph_canonical: '' }))} />
            <MorphSelect speciesCanonical={form.species_canonical} value={form.morph_expected}
              onChange={display => setForm(f => ({ ...f, morph_expected: display, morph_canonical: display ? display.toLowerCase().replace(/\s+/g, '-') : '' }))} />
            <div>
              <Label className="text-zinc-400 mb-2 block">Probabilidad genetica</Label>
              <Input value={form.genetic_probability} onChange={e => setForm(f => ({ ...f, genetic_probability: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100" placeholder="Ej: 50% Pastel, 25% Clown" data-testid="clutch-genetics" />
            </div>
          </div>
        </div>

        {/* Parents */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Pareja Parental</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Sire (Padre)</Label>
              <Input value={form.sire_info} onChange={e => setForm(f => ({ ...f, sire_info: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100" placeholder="Morph, nombre, ID" data-testid="clutch-sire" />
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">Dam (Madre)</Label>
              <Input value={form.dam_info} onChange={e => setForm(f => ({ ...f, dam_info: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100" placeholder="Morph, nombre, ID" data-testid="clutch-dam" />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-zinc-400 mb-2 block">Fotos de padres</Label>
            <div className="flex gap-2 flex-wrap">
              {form.parent_images.map((img, i) => (
                <div key={img} className="relative w-20 h-20 rounded-lg overflow-hidden bg-zinc-800">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, parent_images: f.parent_images.filter((_, j) => j !== i) }))}
                    className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center">
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-lg border-2 border-dashed border-white/10 flex items-center justify-center cursor-pointer hover:border-emerald-500/30">
                {uploading ? <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" /> : <Upload className="w-5 h-5 text-zinc-500" />}
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Detalles de la Camada</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Cantidad esperada</Label>
              <Input type="number" value={form.expected_count} onChange={e => setForm(f => ({ ...f, expected_count: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100" data-testid="clutch-count" />
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">Fecha estimada hatch</Label>
              <Input type="date" value={form.estimated_hatch_date} onChange={e => setForm(f => ({ ...f, estimated_hatch_date: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100" data-testid="clutch-date" />
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">Precio min ($MXN)</Label>
              <Input type="number" value={form.price_range_min} onChange={e => setForm(f => ({ ...f, price_range_min: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100" data-testid="clutch-price-min" />
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">Precio max ($MXN)</Label>
              <Input type="number" value={form.price_range_max} onChange={e => setForm(f => ({ ...f, price_range_max: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100" data-testid="clutch-price-max" />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-zinc-400 mb-2 block">Estado actual</Label>
            <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100" data-testid="clutch-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121215] border-white/10">
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4">
            <Label className="text-zinc-400 mb-2 block">Notas del breeder</Label>
            <Textarea value={form.breeder_notes} onChange={e => setForm(f => ({ ...f, breeder_notes: e.target.value }))}
              className="bg-white/5 border-white/10 text-zinc-100" rows={3} data-testid="clutch-notes" />
          </div>
        </div>

        {/* Waitlist config */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Configuracion de Waitlist</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Modo de waitlist</Label>
              <Select value={form.waitlist_mode} onValueChange={v => setForm(f => ({ ...f, waitlist_mode: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100" data-testid="clutch-waitlist-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121215] border-white/10">
                  <SelectItem value="free">Libre (sin deposito)</SelectItem>
                  <SelectItem value="deposit">Solo con deposito</SelectItem>
                  <SelectItem value="both">Ambos (libre + deposito)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.waitlist_mode === 'deposit' || form.waitlist_mode === 'both') && (
              <div>
                <Label className="text-zinc-400 mb-2 block">Monto de deposito ($MXN)</Label>
                <Input type="number" value={form.deposit_amount} onChange={e => setForm(f => ({ ...f, deposit_amount: e.target.value }))}
                  className="bg-white/5 border-white/10 text-zinc-100" placeholder="Ej: 500" data-testid="clutch-deposit" />
              </div>
            )}
          </div>
        </div>

        <Button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white h-12" data-testid="submit-clutch">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Publicar Camada
        </Button>
      </form>
    </div>
  );
}
